<!-- components/Toast.vue -->
<template>
  <Transition name="fade">
    <div
      v-if="currentToast && isVisible"
      :class="[
        'toast',
        'toast-end',
        verticalPosition ? verticalPosition : 'toast-top',
        'mr-4',
        'z-50',
      ]"
    >
      <div
        :class="[
          'alert',
          currentToast.alertClass,
          'flex justify-between items-center py-2 px-4 my-2 rounded-md',
        ]"
        role="alert"
      >
        <div class="flex items-center">
          <DefaultIcon
            v-if="currentToast.icon"
            :name="currentToast.icon"
            size="h-5 w-5"
            class="mr-2"
          />
          <span>{{ currentToast.text }}</span>
        </div>
        <button
          v-if="currentToast.dismissible"
          @click="hideToast"
          class="btn btn-ghost btn-sm btn-circle hover:bg-base-300"
          aria-label="Close alert"
        >
          <DefaultIcon name="xMark" />
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, defineExpose } from "vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";

const currentToast = ref(null);
const isVisible = ref(false);
const verticalPosition = ref(null);

const hideToast = () => {
  isVisible.value = false;
  setTimeout(() => {
    currentToast.value = null;
  }, 300); // matches transition duration
};

const showToast = ({
  message,
  type = "alert-info",
  icon = "information",
  duration = null,
  dismissible = true,
  position = null, // New parameter for vertical position
}) => {
  // Set vertical position
  verticalPosition.value = position;

  currentToast.value = {
    text: message,
    alertClass: type,
    icon,
    dismissible,
  };
  isVisible.value = true;

  if (duration) {
    setTimeout(hideToast, duration);
  }
};

// Convenience methods with position parameter
const success = (message, duration, position) => {
  showToast({
    message,
    type: "alert-success",
    icon: "check",
    duration,
    position,
  });
};

const error = (message, duration, position) => {
  showToast({
    message,
    type: "alert-error",
    icon: "warning",
    duration,
    position,
  });
};

const warning = (message, duration, position) => {
  showToast({
    message,
    type: "alert-warning",
    icon: "warning",
    duration,
    position,
  });
};

const info = (message, duration, position) => {
  showToast({
    message,
    type: "alert-info",
    icon: "info",
    duration,
    position,
  });
};

// Expose methods for use in other components
defineExpose({
  showToast,
  success,
  error,
  warning,
  info,
  hideToast,
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
